import { useState, useEffect, memo } from 'react';
import { isImageCached, markImageLoaded, preloadImage } from '@/lib/imageCache';

interface UserAvatarProps {
  /** URL of the user's photo */
  photoURL?: string | null;
  /** Display name for alt text and fallback initial */
  displayName?: string;
  /** Size in pixels (default: 40) */
  size?: number;
  /** Additional CSS classes */
  className?: string;
}

/**
 * UserAvatar - A reusable avatar component that gracefully handles image load errors.
 *
 * Shows user's photo if available and loadable, otherwise shows initials.
 * Handles CORS errors, network failures, and broken image URLs gracefully.
 *
 * Uses CSS background-image instead of img tag to avoid Safari's grey
 * loading placeholder. Background images don't show native browser placeholders.
 */
export const UserAvatar = memo(function UserAvatar({
  photoURL,
  displayName = '?',
  size = 40,
  className = '',
}: UserAvatarProps) {
  // Check if image is already cached - if so, render immediately
  const isCached = isImageCached(photoURL);

  const [imageReady, setImageReady] = useState(isCached);
  const [imageError, setImageError] = useState(false);

  // Preload image via JavaScript
  useEffect(() => {
    if (!photoURL || isCached || imageReady || imageError) return;

    let cancelled = false;

    preloadImage(photoURL)
      .then(() => {
        if (!cancelled) {
          markImageLoaded(photoURL);
          setImageReady(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setImageError(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [photoURL, isCached, imageReady, imageError]);

  const showInitials = !photoURL || imageError || !imageReady;
  const initial = displayName?.charAt(0)?.toUpperCase() || '?';

  // Calculate text size based on avatar size
  const mediumOrSmallText = size >= 32 ? 'text-base' : 'text-sm';
  const textSizeClass = size >= 48 ? 'text-lg' : mediumOrSmallText;

  const sizeStyle: React.CSSProperties = {
    width: size,
    height: size,
    minWidth: size,
    minHeight: size,
  };

  // Show initials if no photo, error, or still loading
  if (showInitials) {
    return (
      <div
        className={`rounded-full bg-[#f8e8f8] flex items-center justify-center text-[#8e4585] font-bold overflow-hidden flex-shrink-0 ${textSizeClass} ${className}`}
        style={sizeStyle}
        role="img"
        aria-label={displayName || 'User avatar'}
      >
        {initial}
      </div>
    );
  }

  // Use background-image instead of img tag - Safari doesn't show grey placeholder for background images
  return (
    <div
      className={`rounded-full flex-shrink-0 ${className}`}
      style={{
        ...sizeStyle,
        backgroundImage: `url(${photoURL})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
      role="img"
      aria-label={displayName || 'User avatar'}
    />
  );
});

export default UserAvatar;
